import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Product } from '@spike/product-model';
import { useApiClientWrapper } from 'hooks';
import { fetchProductsThunk, ProductsStatus } from '@spike/products-action';
import ProductComponent from './ProductComponent';
import { AlertNewBooking } from '../UI/AlertNewBooking';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { Spinner } from 'components/UI';
import useNonInitialEffect from '@versiondos/hooks';

interface Props {
    productsUsed?: Array<number>;
    selectedProduct?: Product;
    onSelectProduct: (product?: Product) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            paddingBottom: 18,
            [theme.breakpoints.up('md')]: {
                paddingBottom: 20
            }
        },
        title: {
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '16px',
            marginBottom: 18,
            [theme.breakpoints.up('md')]: {
                marginBottom: 20
            }
        },
        alert: {
            marginBottom: '0px !important',
            height: 53
        },
        alertText: {
            fontSize: 14,
            lineHeight: '20px'
        }
    })
);

export const ProductSelector: FunctionComponent<Props> = props => {
    const classes = useStyles(props);
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const [loading, setLoading] = useState(true);

    const cleanName = (name: string) =>
        name
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z\s]/g, '')
            .toLowerCase()
            .trim();

    const products = useSelector<RootState, Array<Product>>(state => state.products.list)
        .filter(product => product.active)
        .sort((wrapper, otherWrapper) =>
            cleanName(wrapper.businessProduct!.name).localeCompare(cleanName(otherWrapper.businessProduct!.name))
        );

    const productStatus = useSelector<RootState, ProductsStatus>(state => state.products.status);

    useEffect(() => {
        dispatch(fetchProductsThunk(apiClientWrapper));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useNonInitialEffect(() => {
        switch (productStatus) {
            case ProductsStatus.Fetching:
                setLoading(true);
                break;
            case ProductsStatus.FetchSuccess:
                setLoading(false);
                break;
            case ProductsStatus.Error:
                setLoading(false);
        }
    }, [productStatus]);

    const selectHandler = (option: Product) => {
        const selectedProduct = products.find(product => product.businessProduct?.id === option.businessProduct?.id);
        props.onSelectProduct && selectedProduct && props.onSelectProduct(selectedProduct);
    };

    const getProductsUnused = () => {
        if (props.productsUsed) {
            const productsToSelect: Array<Product> = [];
            products.forEach(prod => {
                const productFounded = props.productsUsed?.find(p => p === prod?.businessProduct?.id);
                if (!productFounded) {
                    productsToSelect.push(prod);
                }
            });
            return productsToSelect;
        }
        return products;
    };

    const emptyProductComponent = (
        <Box id="booking_div_no_product_alert">
            <AlertNewBooking
                className={classes.alert}
                bWidth="2px"
                icon={faExclamationTriangle}
                iconSize="lg"
                iconColor="#EAB464"
                bgColor="#F8F5F1"
                bdColor="#BCB8AE"
            >
                <Typography className={classes.alertText}>No product available for this pet</Typography>
            </AlertNewBooking>
        </Box>
    );

    return (
        <Box className={classes.container}>
            {loading ? (
                <Spinner />
            ) : (
                <>
                    {getProductsUnused()?.length === 0 ? (
                        emptyProductComponent
                    ) : (
                        <ProductComponent
                            selectedOption={props.selectedProduct}
                            options={getProductsUnused()}
                            onSelectProduct={selectHandler}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default ProductSelector;
